import { TProductFiltersByNames } from '@cannect/services/resources/products'
import { CURATORSHIP_RANGE_OPTIONS } from '@cannect/constants/curatorshipRanges'

export type TAppliedFilter = {
  key:
    | 'supplier_id'
    | 'brand_id'
    | 'shape_id'
    | 'class_id'
    | 'cannabinoid_name'
    | 'proportion_id'
    | 'translated_name'
    | 'volume_name'
    | 'max_price'
    | 'delivery_by_association'
    | 'category_slug'
    | 'gmp'
    | 'veiculo'
    | 'extraction_method'
    | 'curatorship_range'
  value: string
  label: string
}

const CATEGORY_LABELS: Record<string, string> = {
  'foco-e-performance': 'Cogumelos'
}

const SUPPLIER_LABELS: Record<string, string> = {
  '1': 'Importado',
  '18': 'Nacional',
  '27': 'Suplementos'
}

const DEFAULT_GMP_OPTIONS = [
  { id: 1, name: 'Farmacêutico' },
  { id: 2, name: 'Nutricional' }
]

const DEFAULT_VEHICLE_OPTIONS = [
  { id: 1, name: 'TCM' },
  { id: 2, name: 'Óleo de semente de cânhamo' },
  { id: 3, name: 'Óleo de coco' },
  { id: 4, name: 'Óleo de girassol' },
  //{ id: 5, name: 'Óleo de palma' },
  //{ id: 7, name: 'Azeite de oliva' },
  { id: 8, name: 'Sistemas hidrossolúveis' }
]

const DEFAULT_EXTRACTION_METHOD_OPTIONS = [
  { id: 1, name: 'Alcoólica' },
  { id: 2, name: 'Alcoólica a frio' },
  { id: 3, name: 'Fluído supercrítico' },
  { id: 4, name: 'Hidrocarbonetos' }
]

export const countAppliedFilters = (queryParams: URLSearchParams, _filters: TProductFiltersByNames): number => {
  let count = 0
  const filterKeys = [
    'supplier_id',
    'brand_id',
    'shape_id',
    'class_id',
    'cannabinoid_name',
    'proportion_id',
    'volume_name',
    'gmp',
    'veiculo',
    'extraction_method',
    'curatorship_range'
  ]

  filterKeys.forEach((key) => {
    count += queryParams.getAll(key).length
  })

  const maxPrice = queryParams.get('max_price')
  if (maxPrice && maxPrice !== '2500') {
    count += 1
  }

  const deliveryByAssociation = queryParams.get('delivery_by_association')
  if (deliveryByAssociation === 'true') {
    count += 1
  }

  const categorySlug = queryParams.get('category_slug')
  if (categorySlug) {
    count += 1
  }

  return count
}

export const getAppliedFilters = (queryParams: URLSearchParams, filters: TProductFiltersByNames): TAppliedFilter[] => {
  const filtersList: TAppliedFilter[] = []

  // supplier_id → Importado/Nacional/Suplementos
  queryParams.getAll('supplier_id').forEach((supplierId) => {
    const label = SUPPLIER_LABELS[supplierId]
    if (label) {
      filtersList.push({ key: 'supplier_id', value: supplierId, label })
    }
  })

  queryParams.getAll('brand_id').forEach((brandId) => {
    const brand = filters?.brands?.find((b) => b.id.toString() === brandId)
    if (brand) {
      filtersList.push({ key: 'brand_id', value: brandId, label: brand.name })
    }
  })

  queryParams.getAll('shape_id').forEach((shapeId) => {
    const shape = filters?.shapes?.find((s) => s.id.toString() === shapeId)
    if (shape) {
      filtersList.push({ key: 'shape_id', value: shapeId, label: shape.name })
    }
  })

  queryParams.getAll('class_id').forEach((classId) => {
    const classItem = filters?.classes?.find((c) => c.id.toString() === classId)
    if (classItem) {
      const normalized = (classItem.name || '').trim().toLowerCase()
      const label = normalized === 'outros' || normalized === 'outras' ? 'Perda de peso' : classItem.name
      filtersList.push({ key: 'class_id', value: classId, label })
    }
  })

  // Canabinoides vêm via cannabinoid_name como valores multivalorados (?cannabinoid_name=CBD&cannabinoid_name=Δ9 THCV)
  queryParams.getAll('cannabinoid_name').forEach((name) => {
    const cannabinoid = filters?.cannabinoids?.find((c) => c.name === name)
    if (cannabinoid) {
      filtersList.push({ key: 'cannabinoid_name', value: name, label: cannabinoid.name })
    }
  })

  queryParams.getAll('proportion_id').forEach((proportionId) => {
    const proportion = filters?.proportions?.find((item) => item.id.toString() === proportionId)
    if (proportion) {
      filtersList.push({ key: 'proportion_id', value: proportionId, label: proportion.name })
    }
  })

  queryParams.getAll('volume_name').forEach((volumeName) => {
    const volume = filters?.volumes?.find((v) => v.name === volumeName)
    if (volume) {
      filtersList.push({ key: 'volume_name', value: volumeName, label: volume.name })
    }
  })

  queryParams.getAll('gmp').forEach((gmpName) => {
    const gmpsList = filters?.gmps?.length > 0 ? filters.gmps : DEFAULT_GMP_OPTIONS
    const gmp = gmpsList?.find((g) => g.name === gmpName)
    if (gmp) {
      filtersList.push({ key: 'gmp', value: gmpName, label: gmp.name })
    }
  })

  queryParams.getAll('veiculo').forEach((veiculoName) => {
    const vehiclesList = filters?.vehicles?.length > 0 ? filters.vehicles : DEFAULT_VEHICLE_OPTIONS
    const veiculo = vehiclesList?.find((v) => v.name === veiculoName)
    if (veiculo) {
      filtersList.push({ key: 'veiculo', value: veiculoName, label: veiculo.name })
    }
  })

  queryParams.getAll('extraction_method').forEach((methodName) => {
    const methodsList =
      filters?.extraction_methods?.length > 0 ? filters.extraction_methods : DEFAULT_EXTRACTION_METHOD_OPTIONS
    const method = methodsList?.find((m) => m.name === methodName)
    if (method) {
      filtersList.push({ key: 'extraction_method', value: methodName, label: method.name })
    }
  })

  queryParams.getAll('curatorship_range').forEach((rangeKey) => {
    const option = CURATORSHIP_RANGE_OPTIONS.find((item) => item.id === rangeKey)
    if (option) {
      filtersList.push({ key: 'curatorship_range', value: rangeKey, label: option.name })
    }
  })

  const maxPrice = queryParams.get('max_price')
  if (maxPrice && maxPrice !== '2500') {
    filtersList.push({ key: 'max_price', value: maxPrice, label: `Até R$ ${maxPrice}` })
  }

  const deliveryByAssociation = queryParams.get('delivery_by_association')
  if (deliveryByAssociation === 'true') {
    filtersList.push({ key: 'delivery_by_association', value: 'true', label: 'Associações' })
  }

  const categorySlug = queryParams.get('category_slug')
  if (categorySlug) {
    const categoryLabel = CATEGORY_LABELS[categorySlug] || categorySlug
    filtersList.push({ key: 'category_slug', value: categorySlug, label: categoryLabel })
  }

  return filtersList
}

export const clearAllFilters = (queryParams: URLSearchParams, pathname: string) => {
  const currentLimit = queryParams.get('limit') || '16'
  const currentTranslatedName = queryParams.get('translated_name') || ''

  setTimeout(() => {
    const newParams = new URLSearchParams()
    newParams.set('limit', currentLimit)
    if (currentTranslatedName) {
      newParams.set('translated_name', currentTranslatedName)
    }
    window.history.replaceState({}, '', `${pathname}?${newParams.toString()}`)
  }, 0)
}
